import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LinkSection as LinkSectionType, LinkItem } from '../types/familyLinks';
import { useAuth } from '../contexts/AuthContext';
import AuthGate from './AuthGate';
import LinkSection from './family-links/LinkSection';
import LinkModal from './family-links/AddLinkModal';
import {
  fetchFamilyLinksFromFirestore,
  addFamilyLink,
  deleteFamilyLink,
  updateFamilyLink,
  seedFamilyLinksFromSheets,
} from '../utils/familyLinksFirestore';
import { sampleFamilyLinks } from '../data/sampleFamilyLinks';
import './FamilyLinksPage.css';

const isTestMode = !!(window as any).__TEST_AUTH__;

interface ModalState {
  mode: 'add' | 'edit';
  link?: LinkItem;
  defaultCategory: string;
}

const FamilyLinksPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAllowed } = useAuth();
  const [sections, setSections] = useState<LinkSectionType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);

  useEffect(() => {
    if (!user || !isAllowed) return;

    if (isTestMode) {
      setSections(sampleFamilyLinks);
      setLoading(false);
      return;
    }

    const loadLinks = async () => {
      try {
        setLoading(true);
        let linksData = await fetchFamilyLinksFromFirestore();

        if (linksData.length === 0) {
          linksData = await seedFamilyLinksFromSheets();
        }

        setSections(linksData);
        setError(null);
      } catch (err) {
        console.error('Failed to load links:', err);
        setError('Failed to load links. Please try again later.');
        setSections([]);
      } finally {
        setLoading(false);
      }
    };

    loadLinks();
  }, [user, isAllowed]);

  const categories = sections.map(s => s.title);

  const handleAdd = (category: string) => {
    setModal({ mode: 'add', defaultCategory: category });
  };

  const handleEdit = (link: LinkItem, category: string) => {
    setModal({ mode: 'edit', link, defaultCategory: category });
  };

  const handleDelete = async (link: LinkItem) => {
    if (isTestMode) {
      setSections(prev =>
        prev
          .map(s => ({ ...s, links: s.links.filter(l => l.id !== link.id) }))
          .filter(s => s.links.length > 0)
      );
      return;
    }
    try {
      await deleteFamilyLink(link.id);
      const data = await fetchFamilyLinksFromFirestore();
      setSections(data);
    } catch (err) {
      console.error('Failed to delete link:', err);
    }
  };

  const handleSave = async (data: { id: string; name: string; url: string; category: string }) => {
    if (isTestMode) {
      setSections(prev => {
        const newLink: LinkItem = { id: data.id, name: data.name, url: data.url };
        let updated = prev.map(s => ({
          ...s,
          links: s.links.filter(l => l.id !== data.id),
        }));
        const target = updated.find(s => s.title === data.category);
        if (target) {
          updated = updated.map(s =>
            s.title === data.category
              ? { ...s, links: [...s.links, newLink] }
              : s
          );
        } else {
          updated = [...updated, { title: data.category, links: [newLink] }];
        }
        return updated.filter(s => s.links.length > 0);
      });
      setModal(null);
      return;
    }

    try {
      if (modal?.mode === 'edit') {
        await updateFamilyLink(data.id, { name: data.name, url: data.url, category: data.category });
      } else {
        await addFamilyLink(data.name, data.url, data.category);
      }
      setModal(null);
      const freshData = await fetchFamilyLinksFromFirestore();
      setSections(freshData);
    } catch (err) {
      console.error('Failed to save link:', err);
    }
  };

  return (
    <div className="family-links-page">
      <div className="family-links-header">
        <button className="family-links-back" onClick={() => navigate('/')}>
          ← Back
        </button>
      </div>
      <AuthGate>
        <main className="family-links-main">
          {loading && (
            <div className="family-links-loading-state">
              <div className="family-links-loading-spinner" />
              <p>Loading links...</p>
            </div>
          )}

          {error && (
            <div className="family-links-error-state">
              <div className="family-links-error-icon">!</div>
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && sections.map(section => (
            <LinkSection
              key={section.title}
              section={section}
              onAdd={handleAdd}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </main>
      </AuthGate>

      {modal && (
        <LinkModal
          editing={modal.mode === 'edit' ? (modal.link ?? null) : null}
          defaultCategory={modal.defaultCategory}
          categories={categories}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};

export default FamilyLinksPage;
